<template>
  <div>
    <template v-if="loading">
      <div class="grid gap-2" aria-hidden="true">
        <div v-for="i in 3" :key="i" class="flex flex-col border border-border rounded-lg">
          <div class="p-4 gap-4 flex items-center">
            <Skeleton variant="circular" :width="16" :height="16" class="flex-none" />
            <Skeleton variant="rounded" :width="56" :height="56" class="flex-none" />
            <div class="grid gap-2 flex-1 min-w-0">
              <Skeleton variant="text" class="max-w-[140px]" />
              <Skeleton variant="text" class="max-w-[200px] h-3" />
            </div>
            <Skeleton v-if="!isB2C" variant="text" class="w-12 flex-none" />
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <RadioGroup v-model="selected">
        <div class="grid gap-2">
          <RadioGroupOption
            v-for="option of options"
            :key="option.method"
            v-slot="{ checked }"
            :value="option.method"
            :disabled="option.disabled">
            <PaymentMethod
              :method="option.method"
              :percentage="option.percentage"
              :checked="checked"
              :disabled="option.disabled"
              @click="updatePaymentMethod(option)" />
          </RadioGroupOption>
        </div>
      </RadioGroup>
      <ErrorMessage name="booking.paymentMethod" />
    </template>
  </div>
  <template v-if="!isB2C">
    <Checkbox
      name="addPaymentAndOrBookingReferences"
      :label="
        $t(showVatNumber ? 'Add payment-, booking references or VAT number' : 'Add payment- or booking references')
      " />
    <template v-if="showReferences">
      <div class="flex flex-col">
        <Input name="booking.paymentReference" :label="$t('Payment reference')" />

        <div class="text-muted-foreground text-xs mt-2">
          {{ $t("Reference we will print on the invoice (optional)") }}
        </div>
      </div>
      <div class="flex flex-col">
        <Input name="booking.bookingReference" :label="$t('Booking reference')" />
        <div class="text-muted-foreground text-xs mt-2">
          {{ $t("Booking reference will be shown in the bookingsoverview (optional)") }}
        </div>
      </div>
      <Input v-if="showVatNumber" name="booking.vatNumber" :label="$t('VAT Number')" />
    </template>
  </template>
</template>
<script setup lang="ts">
import { ref, watch, computed, onMounted } from "vue";
import { RadioGroup, RadioGroupOption } from "@headlessui/vue";
import { PaymentMethod } from ".";
import { useUserStore } from "@/store/user.js";
import { useCartStore } from "@/store/cart.ts";
import { methods } from ".";
import { mergePaymentMethodOptions } from "./mergeOptions";
import { Checkbox, Input, Skeleton } from "@xs2event/js-lib/components";
import ErrorMessage from "@/components/validation/ErrorMessage.vue";
import { useField } from "@xs2event/js-lib/validation";

import { api } from "@/helpers/api";
import brandconfig from "@/brandconfig";
import { useDistributorStore } from "@/store/distributor";

const addPaymentAndOrBookingReferencesField = useField<boolean>("addPaymentAndOrBookingReferences");

const showReferences = computed(() => addPaymentAndOrBookingReferencesField.value.value);

const isB2C = brandconfig.BRAND !== "xs2event";

const cartStore = useCartStore();
const userStore = useUserStore();

const distributorStore = useDistributorStore();

const showVatNumber = distributorStore.distributor && !distributorStore.distributor?.finance.vat_number;

const options = ref([]);
const selected = ref<string>();
const loading = ref(true);

const field = useField<string | undefined>("booking.paymentMethod");

watch(selected, (value) => {
  field.setValue(value);
});

const initOptions = async () => {
  loading.value = true;
  try {
    const userOptionsReq = userStore.getPaymentMethods();
    const cartOptionsReq = cartStore.getPaymentMethods();
    const userOptions = await userOptionsReq;
    const cartOptions = await cartOptionsReq;

    options.value = mergePaymentMethodOptions(userOptions, cartOptions, methods);

    const enabled = options.value.find((option) => !option.disabled);
    if (enabled) {
      selected.value = enabled.method;
      cartStore.paymentSelected = selected.value;
    }
  } finally {
    loading.value = false;
  }
};
const userName = computed(() => userStore.username);
const reservationId = computed(() => cartStore.reservationId);

watch([userName, reservationId], initOptions);

onMounted(initOptions);

const updatePaymentDetails = (payload) => {
  console.log(payload);

  api.put(`/reservations/${cartStore.reservationId}/pay-details`, payload).then((res) => {
    cartStore.payDetails = res.data;
  });
};

function updatePaymentMethod(option) {
  cartStore.paymentSelected = option.method;
}

watch(selected, (selected) => {
  const payload = {
    payment_method: selected,
    payment_reference: cartStore.paymentReference,
    general_terms_url: brandconfig.GENERAL_TERMS_LINK,
  };
  updatePaymentDetails(payload);
});
</script>
